import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useV2Auth } from '@/context/V2AuthContext';
import {
  FocusedJourneyLayout, MaskContainer, MaskContext, MaskHeadline,
  PrimaryAction, SecondaryAction, MaskBack,
} from '@/components/mask/MaskSystem';

/* MOMENTS 1.0 — Empfänger-Journey: E01 FÜR DICH → ein Carma nach dem anderen → BLEIBT → BEWAHREN */

export default function MomentExperienceV2() {
  const { user, API_V2 } = useV2Auth();
  const { token } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [phase, setPhase] = useState('intro'); // intro | read | done | keep | kept
  const [index, setIndex] = useState(0);
  const [nextReady, setNextReady] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState(null);
  const firedRef = useRef(false);
  const progressKey = `cc_moment_${token}`;

  const numWord = (n) => t(`v2.numWords.${n}`, { defaultValue: String(n) });

  useEffect(() => {
    axios.get(`${API_V2}/moments/experience/${token}`)
      .then((res) => {
        setData(res.data);
        const saved = parseInt(localStorage.getItem(progressKey) || '0', 10);
        if (saved > 0 && saved < res.data.contributions.length) { setIndex(saved); setPhase('read'); }
      })
      .catch((err) => setError(err.response?.data?.detail || t('v2.mexp.notFound')));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_V2, token]);

  // Adaptive Lesezeit pro Beitrag, dann sehr dezent: "Da ist noch jemand."
  useEffect(() => {
    if (phase !== 'read' || !data) return undefined;
    setNextReady(false);
    const c = data.contributions[index];
    const secs = Math.min(20, Math.max(4, Math.round(c.message.length / 16)));
    const timer = setTimeout(() => setNextReady(true), secs * 1000);
    localStorage.setItem(progressKey, String(index));
    return () => clearTimeout(timer);
  }, [phase, index, data, progressKey]);

  const finish = () => {
    localStorage.removeItem(progressKey);
    if (!firedRef.current) {
      firedRef.current = true;
      axios.post(`${API_V2}/moments/experience/${token}/event`, {}).catch(() => {});
    }
    setPhase('done');
  };

  const next = () => {
    if (index + 1 >= data.contributions.length) finish();
    else setIndex(index + 1);
  };

  const keep = async () => {
    if (claiming) return;
    setClaiming(true); setClaimError(null);
    try {
      await axios.post(`${API_V2}/moments/experience/${token}/keep`, {}, { withCredentials: true });
      setPhase('kept');
    } catch (err) {
      setClaimError(err.response?.data?.detail || t('v2.common.errorShort'));
    } finally { setClaiming(false); }
  };

  if (error) {
    return (
      <FocusedJourneyLayout>
        <MaskContainer maskKey="err" bgWord={t('v2.bgWords.moment')} align="center" bgShift="-8%">
          <MaskHeadline>{t('v2.mexp.notReady')}</MaskHeadline>
          <p className="mask-enter mask-enter-delay-2 text-base text-[var(--muted)] mb-10">{error}</p>
          <SecondaryAction onClick={() => navigate('/')} data-testid="experience-error-home">{t('v2.common.whatIs')}</SecondaryAction>
        </MaskContainer>
      </FocusedJourneyLayout>
    );
  }
  if (!data) return null;

  const c = data.contributions[index];

  return (
    <FocusedJourneyLayout>
      {phase === 'intro' && (
        <MaskContainer maskKey="e01" bgWord={t('v2.bgWords.fuerDich')} align="center" bgShift="-10%" data-testid="mexp-intro">
          <MaskContext>{data.occasion}</MaskContext>
          <MaskHeadline>
            {data.count === 1 ? t('v2.mexp.introOne') : t('v2.mexp.introMany', { num: numWord(data.count) })}
          </MaskHeadline>
          <p className="mask-enter mask-enter-delay-2 text-base text-[var(--muted)] mb-10" data-testid="mexp-take-time">{t('v2.mexp.takeTime')}</p>
          <PrimaryAction onClick={() => setPhase('read')} data-testid="mexp-start-btn">{t('v2.mexp.start')}</PrimaryAction>
        </MaskContainer>
      )}

      {phase === 'read' && c && (
        <div key={c.id} className="relative min-h-screen flex flex-col" data-testid="mexp-read">
          {index > 0 && <MaskBack onClick={() => setIndex(index - 1)} data-testid="mexp-prev-btn" />}
          <div className="mask-bg-word mask-bg-top" aria-hidden="true"><span style={{ transform: 'translateX(-8%)' }}>{t('v2.bgWords.gesehen')}</span></div>
          <div className="relative z-10 flex-1 flex flex-col justify-center w-full max-w-xl mx-auto px-6 sm:px-8 py-24">
            <MaskContext>{t('v2.common.from', { name: c.name })}</MaskContext>
            <p className="mask-enter mask-enter-delay-1 mask-letter text-xl sm:text-2xl text-[var(--text)]" data-testid="mexp-message">
              {c.message}
            </p>
            {c.signature && <img src={c.signature} alt={c.name} className="h-14 mt-4" data-testid="mexp-signature" />}
            <div className="mt-14 min-h-[80px]">
              {nextReady && (
                <div className="mask-enter">
                  {index + 1 < data.contributions.length ? (
                    <>
                      {index === 0 && (
                        <p className="text-sm text-[var(--muted)] mb-5" data-testid="mexp-transition">{t('v2.mexp.transition')}</p>
                      )}
                      <button type="button" onClick={next} data-testid="mexp-next-btn"
                        className="text-left group">
                        <span className="block mask-headline text-xl text-[var(--text)]/80 mb-1">{t('v2.mexp.oneMore')}</span>
                        <span aria-hidden="true" className="inline-block text-[var(--muted)] transition-transform group-hover:translate-x-1">→</span>
                      </button>
                    </>
                  ) : (
                    <button type="button" onClick={next} data-testid="mexp-finish-btn"
                      className="text-sm text-[var(--muted)] hover:text-[var(--text)] py-3 px-1 transition-colors underline underline-offset-4 decoration-[var(--muted)]/40">
                      {t('v2.common.continue')}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {phase === 'done' && (
        <MaskContainer maskKey="done" bgWord={t('v2.bgWords.bleibt')} align="center" bgShift="-8%" data-testid="mexp-done">
          <MaskHeadline>{t('v2.mexp.doneHeadline')}</MaskHeadline>
          <p className="mask-enter mask-enter-delay-2 text-base text-[var(--muted)] leading-relaxed mb-12 max-w-md mx-auto">
            {data.count === 1 ? t('v2.mexp.doneOne') : t('v2.mexp.doneMany', { num: numWord(data.count) })}
          </p>
          <SecondaryAction onClick={() => setPhase('keep')} data-testid="mexp-to-keep-btn">{t('v2.common.continue')}</SecondaryAction>
        </MaskContainer>
      )}

      {phase === 'keep' && (
        <MaskContainer maskKey="keep" bgWord={t('v2.bgWords.bewahren')} align="center" bgShift="-8%" data-testid="mexp-keep">
          {data.claimed ? (
            <>
              <MaskHeadline>{t('v2.mexp.alreadyYours')}</MaskHeadline>
              <SecondaryAction onClick={() => navigate(user ? '/bewahrt' : '/')} data-testid="mexp-claimed-btn">{user ? t('v2.mexp.toKept') : t('v2.common.whatIs')}</SecondaryAction>
            </>
          ) : (
            <>
              <MaskHeadline>{t('v2.keep.headline')}</MaskHeadline>
              <p className="mask-enter mask-enter-delay-2 text-base text-[var(--muted)] leading-relaxed mb-10 max-w-md mx-auto">
                {t('v2.mexp.keepText')}
              </p>
              {claimError && <p className="text-sm text-red-700/80 mb-4" data-testid="mexp-keep-error">{claimError}</p>}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-3">
                {user ? (
                  <PrimaryAction onClick={keep} disabled={claiming} data-testid="mexp-keep-btn">{claiming ? t('v2.common.loading') : t('v2.mexp.keepBtn')}</PrimaryAction>
                ) : (
                  <PrimaryAction onClick={() => navigate(`/auth?redirect=/moment/erleben/${token}`)} data-testid="mexp-register-btn">{t('v2.shared.createPlace')}</PrimaryAction>
                )}
                <SecondaryAction onClick={() => navigate('/')} data-testid="mexp-later-btn">{t('v2.common.maybeLater')}</SecondaryAction>
              </div>
            </>
          )}
        </MaskContainer>
      )}

      {phase === 'kept' && (
        <MaskContainer maskKey="kept" bgWord={t('v2.bgWords.bewahrt')} align="center" bgShift="-6%" data-testid="mexp-kept">
          <MaskHeadline>{t('v2.experience.keptLabel')}</MaskHeadline>
          <p className="mask-enter mask-enter-delay-2 text-base text-[var(--muted)] leading-relaxed mb-10 max-w-md mx-auto">
            {t('v2.mexp.keptText')}
          </p>
          <PrimaryAction onClick={() => navigate('/bewahrt')} data-testid="mexp-kept-btn">{t('v2.mexp.toKept')}</PrimaryAction>
        </MaskContainer>
      )}
    </FocusedJourneyLayout>
  );
}
